import { FieldInputType, LenderFieldMap, PortalConfiguration } from '@trnsact/trnsact-shared-types';
import _ from 'lodash';
import moment from 'moment';
import { DocumentRow, LenderMappingsFormValues, StructuredMappings } from '../types';
import { PERSONAL_GUARANTOR_FIELD_NAMES } from '../constants/personalGuarantorFields';
import {
  getCountriesArray,
  getDaysArray,
  getMonthsArray,
  getStatesArray,
  getYearsArray,
  legalStructureOption,
} from 'utils';
import { keyBy } from 'utils/arrayUtils';
import { SelectOption } from 'global';
import { MUITableColumn } from 'components/shared/MUITable';
import { Box, Typography, Switch } from '@mui/material';
import { DateFormats } from 'constants/common';
import { formatSSNInput } from 'formatters';

const isValidDate = (date: any): boolean => {
  return !isNaN(Date.parse(String(date)));
};

const transformMappedValueForFieldType = (fieldInputType: string | undefined, mappedValue: any): any => {
  if (!mappedValue) return mappedValue;
  const strValue = String(mappedValue);

  switch (fieldInputType) {
    case 'YEAR':
      if (typeof mappedValue === 'string' && isValidDate(mappedValue)) {
        try {
          return String(moment(mappedValue).year());
        } catch {
          return mappedValue;
        }
      }
      return mappedValue;
    case 'MONTH':
      if (typeof mappedValue === 'string' && isValidDate(mappedValue)) {
        try {
          return moment(mappedValue).format('MMMM');
        } catch {
          return mappedValue;
        }
      }
      return mappedValue;
    case 'DAY':
      if (typeof mappedValue === 'string' && strValue.length > 2) {
        try {
          return String(moment(mappedValue).date());
        } catch {
          return mappedValue;
        }
      }
      return mappedValue;
    case 'SSN':
      return formatSSNInput(strValue);
    default:
      return mappedValue;
  }
};

export const prepareMappingsFromResponse = (
  lenderFieldMappings: LenderFieldMap[],
  transactionSummary: string
): LenderFieldMap[] => {
  return lenderFieldMappings.map(mapping => {
    const normalizedOptionSets =
      mapping.details?.fieldInputType === FieldInputType.OptionSet && Array.isArray(mapping.details.optionSets)
        ? _.uniqBy(mapping.details.optionSets, 'displayName')
        : (mapping.details?.optionSets ?? null);

    let mappedValue =
      mapping.details?.dcrPropertySource === 'vendorOpportunity.transactionSummary'
        ? transactionSummary
        : mapping.mappedValue;

    mappedValue = transformMappedValueForFieldType(mapping.details?.fieldInputType!, mappedValue);

    return {
      ...mapping,
      mappedValue,
      details: {
        ...mapping.details,
        optionSets: normalizedOptionSets,
      },
    };
  });
};

export const getStructuredMappings = (lenderFieldMappings: LenderFieldMap[]): StructuredMappings => {
  const groupedByCategory = structureFields(lenderFieldMappings, 'category');
  Object.entries(groupedByCategory).forEach(
    entry => (groupedByCategory[entry[0]] = structureFields(entry[1] as any[], 'section'))
  );
  return sortByOrdinal(groupedByCategory);
};

const structureFields = (fields: any[], key: string) => {
  let obj: any = {};
  fields.forEach(field => {
    const value = field.details?.[key];
    if (!value) {
      obj['DEFAULT'] = obj['DEFAULT'] ? [...obj['DEFAULT'], field] : [field];
    } else {
      obj[value] = obj[value] ? [...obj[value], field] : [field];
    }
  });

  return obj;
};

const sortByOrdinal = (mapping: any) => {
  let sortedMapping: any = {};

  Object.entries(mapping)
    .sort((a, b) => {
      let A = null;
      let B = null;

      try {
        A = (a[1] as any).DEFAULT[0].details?.ordinal;
      } catch (e) {
        A = null;
      }

      try {
        B = (b[1] as any).DEFAULT[0].details?.ordinal;
      } catch (e) {
        B = null;
      }

      if (A === null) return 1;
      if (B === null) return -1;

      return A < B ? -1 : 1;
    })
    .forEach(entry => (sortedMapping[entry[0]] = entry[1]));

  return sortedMapping;
};

export const formatDCRProperty = (property?: string | null) => {
  if (!property) return null;

  let strProps = property.split('|').map(prop =>
    prop
      .split('.')
      .filter(prop => prop !== 'INTETIONALLY_BAD_MAPPING' && prop !== 'BAD_INPUT')
      .map(prop => {
        let newProp = '';
        newProp = prop.replace('[0]', '');
        newProp = prop.replace('[1]', '');
        newProp = prop.replace('[2]', '');

        if (newProp === 'creditApp') newProp = 'Credit Application';
        else if (newProp.includes('creditApplicationOwner')) newProp = 'Owner';
        else if (newProp.includes('reference')) {
          try {
            const referenceJson = JSON.parse(property.match(/reference\[(.*?)\]/)?.[1] ?? '{}');
            newProp = `Reference (${referenceJson.type})`;
          } catch (e) {
            console.log('Problem getting reference type');
            newProp = 'Reference';
          }
        } else if (newProp.includes('equipments')) newProp = 'Equipment';
        else if (newProp === 'vendorOpportunity') newProp = 'Opportunity';
        else if (newProp === 'userProfile') newProp = 'Current User';
        else if (newProp === 'null') newProp = '';
        else
          newProp = newProp.replace(/([A-Z])/g, ' $1').replace(/^./, function (str) {
            return str.toUpperCase();
          });

        if (newProp === 'Ssn') {
          return 'SSN';
        } else if (newProp.includes('Fedaral')) {
          newProp = newProp.replace('Fedaral', 'Federal');
        }
        return newProp;
      })
  );

  return strProps.map(prop => prop.join(': '));
};

export const getTrnsactDisplayName = (field: LenderFieldMap, portalConfiguration: PortalConfiguration) => {
  let displayName = undefined;
  try {
    const keyToFind = field.details?.dcrPropertySource?.replace('creditApp.', '').replace('[', '.').replace(']', '');
    const pcwSections = portalConfiguration.jsonDefinition.sections as any[];
    pcwSections.forEach(section => {
      section.blocks.forEach((block: any) => {
        const matchingField = block.fields.find((field: any) => {
          return field.key === keyToFind || field.ambiguousKey === keyToFind;
        });
        if (matchingField) {
          displayName = matchingField.ocaConfig.config.displayLabel.en;
        }
      });
    });
    if (!displayName) {
      displayName = field.details?.displayName;
    }
    return displayName;
  } catch (e: any) {
    console.log(
      `Error finding display name through portal config for key ${field.details?.dcrPropertySource}, returning default displayName: ${e.message}`
    );
  }
  return field.details?.displayName;
};

export const getTrnsactDisplayValue = (field: LenderFieldMap) => {
  if (field.details?.dcrPropertySource?.includes('legalStructure')) {
    const selectedLso = legalStructureOption.find(lso => {
      return lso.value === field.originalMappedValue;
    });
    if (selectedLso) {
      return selectedLso.label;
    }
  }
  if (field.originalMappedValue === true) {
    return 'Yes';
  }
  if (field.originalMappedValue === false) {
    return 'No';
  }
  return field.originalMappedValue ? field.originalMappedValue : ' ';
};

export const getInputProps = (lenderField: LenderFieldMap) => {
  if (lenderField?.details?.rulesConfiguration?.configuration) {
    const inputProps = lenderField?.details?.rulesConfiguration?.configuration;
    return inputProps;
  }
  return {};
};

/** Escapes dots and brackets so RHF treats the key as a flat segment. */
export const sanitizeFormKey = (key: string): string =>
  key.replace(/\./g, '_dot_').replace(/\[/g, '_lb_').replace(/\]/g, '_rb_');

export const getLenderMappingsFormDefaultValues = (dynamicLenderMappings: LenderFieldMap[]) => {
  const mappings: Record<string, { lenderProperty: string; mappedValue: any }> = {};

  dynamicLenderMappings.forEach(mapping => {
    const key = sanitizeFormKey(mapping.lenderProperty!);
    mappings[key] = {
      lenderProperty: mapping.lenderProperty!,
      mappedValue: mapping.mappedValue ?? '',
    };
  });

  return { mappingsByLenderProperty: mappings };
};

export const isCategoryVisible = (category: string, sections: Record<string, LenderFieldMap[]>): boolean => {
  return category !== 'DEFAULT' && Object.values(sections).some(fields => fields.some(field => !field.details?.hidden));
};

// Real dcrPropertySource shape is `creditApp.creditApplicationOwner[N].<field>` (confirmed against live data).
const OWNER_DCR_PROPERTY_SOURCE_REGEX = /(?:^|\.)creditApplicationOwner\[\d+\]\.(.+)$/;

export const isPersonalGuarantorField = (field: LenderFieldMap): boolean => {
  const match = field.details?.dcrPropertySource?.match(OWNER_DCR_PROPERTY_SOURCE_REGEX);
  if (!match) return false;
  return PERSONAL_GUARANTOR_FIELD_NAMES.has(match[1]);
};

export const filterOutPersonalGuarantorFields = (mappings: LenderFieldMap[]): LenderFieldMap[] =>
  mappings.filter(field => !isPersonalGuarantorField(field));

export const calculateCategoryStats = (
  fields: LenderFieldMap[],
  formValues: LenderMappingsFormValues['mappingsByLenderProperty']
) => {
  const isFilled = (field: LenderFieldMap) => {
    const value = formValues?.[sanitizeFormKey(field.lenderProperty!)]?.mappedValue;
    return !!value || value === 0;
  };
  return {
    total: fields.length,
    requiredTotal: fields.filter(f => f.details?.required === 'REQUIRED').length,
    filled: fields.filter(isFilled).length,
    requiredFilled: fields.filter(f => f.details?.required === 'REQUIRED' && isFilled(f)).length,
  };
};

export const getSelectMappingFieldOptions = (field: LenderFieldMap): SelectOption[] => {
  const type = field.details?.fieldInputType;

  if (type === 'YEAR') {
    return getYearsArray().map(yr => ({ label: yr.toString(), value: yr.toString() }));
  }
  if (type === 'MONTH') {
    return getMonthsArray().map(month => ({ label: month.long, value: month.long }));
  }
  if (type === 'DAY') {
    return getDaysArray().map(day => ({ label: day.toString(), value: day.toString() }));
  }
  if (type === 'STATE') {
    const stateIndex = field.mappedValue?.length <= 2 ? 'abbr' : 'name';
    return getStatesArray().map(state => ({
      label: state[stateIndex],
      value: state[stateIndex],
    }));
  }
  if (type === 'STATE_ABBR') {
    return getStatesArray().map(state => ({
      label: state['abbr'],
      value: state['abbr'],
    }));
  }
  if (type === 'COUNTRY') {
    const countryIndex = field.mappedValue?.length <= 2 ? 'code' : 'name';
    return getCountriesArray().map(country => ({
      label: country[countryIndex],
      value: country[countryIndex],
    }));
  }
  if (type === 'COUNTRY_CODE') {
    return getCountriesArray().map(country => ({
      label: `${country['name']} (${country['code']})`,
      value: country['code'],
    }));
  }

  return (
    field.details?.optionSets?.map((option: any) => ({
      label: option.displayName,
      value: option.lenderValue,
    })) || []
  );
};

export const getDocumentsAccessTableColumns = ({
  allowedDocuments,
  onToggle,
}: {
  allowedDocuments: string[];
  onToggle: (documentId: string) => void;
}): MUITableColumn<DocumentRow>[] => [
  { header: 'File Name', renderKey: 'docName' },
  { header: 'Uploaded By', renderKey: 'createdBy' },
  {
    header: 'Uploaded On',
    renderKey: 'createdDateTime',
    renderCell: ({ createdDateTime }: DocumentRow) =>
      createdDateTime ? moment(createdDateTime).format(DateFormats.DateTimeShort12Hour) : '',
  },
  {
    header: 'Type',
    renderKey: 'docType',
  },
  { header: 'Description', renderKey: 'docDescription' },
  {
    header: 'Lender Access',
    renderKey: 'lenderAccess',
    renderCell: (row: DocumentRow) => {
      const isDTFSLenderCreditApp = row.documentId === 'dtfs-credit-app-doc-id';

      if (isDTFSLenderCreditApp) {
        return (
          <Typography variant="body2" fontWeight={500}>
            Allow
          </Typography>
        );
      }

      const isAllowed = allowedDocuments.includes(row.documentId);
      return (
        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
          <Switch size="small" checked={isAllowed} onChange={() => onToggle(row.documentId)} color="primary" />
          <Typography variant="body2" fontWeight={500} color={isAllowed ? 'primary' : 'textSecondary'}>
            {isAllowed ? 'Allow' : 'Deny'}
          </Typography>
        </Box>
      );
    },
  },
];

export const formatDocumentsToTableData = (documents: any[]): DocumentRow[] =>
  documents.map((doc: any) => ({
    id: doc.documentId,
    documentId: doc.documentId,
    docName: doc.docName ?? '',
    createdBy: doc.createdBy ?? '',
    createdDateTime: doc.createdDateTime ?? '',
    docType: doc.docType ?? '',
    docDescription: doc.docDescription ?? '',
    lenderAccess: '',
  }));

export const filterDocumentsBySearchTerm = (docs: DocumentRow[], searchTerm: string): DocumentRow[] => {
  if (!searchTerm || searchTerm.length < 3) return docs;

  const q = searchTerm.toLowerCase();
  const keysToFilter: (keyof DocumentRow)[] = ['docName', 'createdBy', 'docType', 'docDescription'];

  return docs.filter(d =>
    keysToFilter.some(k =>
      String(d[k] ?? '')
        .toLowerCase()
        .includes(q)
    )
  );
};
